import { Goal, GoalProgressCount, ID, Task } from '@griply/types';

import { isCountableGoalTask } from '../task-predicates';

const emptyCount: GoalProgressCount = { completed: 0, total: 0 };

export const buildGoalProgressCounts = (
  tasks: Task[],
  goals: Goal[],
): Map<ID, GoalProgressCount> => {
  const directCounts = new Map<ID, GoalProgressCount>();

  tasks.forEach((task) => {
    if (!task.goalId || !isCountableGoalTask(task)) {
      return;
    }

    const current = directCounts.get(task.goalId) ?? emptyCount;
    directCounts.set(task.goalId, {
      completed: current.completed + (task.completedAt ? 1 : 0),
      total: current.total + 1,
    });
  });

  const childrenByParent = new Map<ID, Goal[]>();
  goals.forEach((goal) => {
    const parentId = goal.parentIds?.[goal.parentIds.length - 1];
    if (!parentId) {
      return;
    }

    const siblings = childrenByParent.get(parentId) ?? [];
    siblings.push(goal);
    childrenByParent.set(parentId, siblings);
  });

  const rolledCounts = new Map<ID, GoalProgressCount>();
  const inProgress = new Set<ID>();

  const rollUp = (goalId: ID): GoalProgressCount => {
    const cached = rolledCounts.get(goalId);
    if (cached) {
      return cached;
    }

    // corrupt parentIds can form a cycle — a re-entered goal contributes nothing
    if (inProgress.has(goalId)) {
      return emptyCount;
    }

    inProgress.add(goalId);

    const rolled = (childrenByParent.get(goalId) ?? []).reduce(
      (accumulator, child) => {
        const childCount = rollUp(child.id);

        return {
          completed: accumulator.completed + childCount.completed,
          total: accumulator.total + childCount.total,
        };
      },
      directCounts.get(goalId) ?? emptyCount,
    );

    inProgress.delete(goalId);
    rolledCounts.set(goalId, rolled);

    return rolled;
  };

  const counts = new Map<ID, GoalProgressCount>();
  goals.forEach((goal) => {
    const count = rollUp(goal.id);
    if (!count.total) {
      return;
    }

    counts.set(goal.id, count);
  });

  return counts;
};
